// pages/AddMoney.js - Child requests money deposit
import React, { useState } from 'react';
import { ArrowLeft, DollarSign, PiggyBank, Coins, Star, Gift, Heart, Zap, AlertCircle, CheckCircle } from 'lucide-react';
import { Button, Input, Card } from '../components/ui';

export const AddMoney = ({ child, family, guardian, onNavigate, onDepositRequested }) => {
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    source: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

    const API_BASE_URL = 'https://growthsprouts-server.vercel.app/api';


  // Predefined money sources for quick selection
  const moneySources = [
    { id: 'allowance', name: 'Allowance', icon: '💰', description: 'Weekly allowance' },
    { id: 'chores', name: 'Chores', icon: '🏠', description: 'Completed household tasks' },
    { id: 'gift', name: 'Gift Money', icon: '🎁', description: 'Birthday or holiday money' },
    { id: 'tooth_fairy', name: 'Tooth Fairy', icon: '🦷', description: 'Lost tooth payment' },
    { id: 'good_grades', name: 'Good Grades', icon: '⭐', description: 'Academic achievement' },
    { id: 'extra_chores', name: 'Extra Help', icon: '💪', description: 'Going above and beyond' },
    { id: 'lemonade_stand', name: 'Earned Money', icon: '🍋', description: 'Business or job earnings' },
    { id: 'other', name: 'Other', icon: '❓', description: 'Something else' }
  ];

  // Quick amount buttons
  const quickAmounts = [1, 5, 10, 20, 25, 50];

  const childName = child?.first_name || child?.firstName || 'Little Saver';
  const childAvatar = child?.avatar || 'sprout';

  const validateForm = () => {
    const newErrors = {};

    // Amount validation
    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else {
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        newErrors.amount = 'Please enter a valid amount';
      } else if (amount > 500) {
        newErrors.amount = 'Amount cannot exceed $500';
      } else if (amount < 0.25) {
        newErrors.amount = 'Minimum amount is $0.25';
      }
    }

    // Source validation
    if (!formData.source) {
      newErrors.source = 'Please select where this money came from';
    }

    // Description validation for "other" source
    if (formData.source === 'other' && !formData.description.trim()) {
      newErrors.description = 'Please describe where this money came from';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  setIsSubmitting(true);
  setErrors({});

  try {
    console.log('AddMoney: Requesting deposit:', {
      childId: child.id,
      amount: parseFloat(formData.amount),
      source: formData.source,
      description: formData.description || moneySources.find(s => s.id === formData.source)?.name
    });

    // Determine who is making the request
    const requestData = {
      amount: parseFloat(formData.amount),
      description: formData.description || moneySources.find(s => s.id === formData.source)?.name,
      source: formData.source,
      created_by: child.id // Use child's ID as created_by when child is making the request
    };

    console.log('AddMoney: Sending request data:', requestData);

    // API call to create pending deposit
    const response = await fetch(`${API_BASE_URL}/children/${child.id}/deposits`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    console.log('AddMoney: Response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('AddMoney: Server error:', errorData);
      throw new Error(errorData.error || `Server error: ${response.status}`);
    }

    const result = await response.json();
    console.log('AddMoney: Response result:', result);

    if (result.success) {
      console.log('AddMoney: Deposit request created successfully:', result.data);
      
      // Show success state
      setShowSuccess(true);
      
      // Call parent callback if provided
      if (onDepositRequested) {
        onDepositRequested(result.data);
      }
      
      // Redirect after showing success message
      setTimeout(() => {
        onNavigate('childDashboard', child);
      }, 2000);
    } else {
      throw new Error(result.error || 'Failed to create deposit request');
    }

  } catch (error) {
    console.error('AddMoney: Error creating deposit request:', error);
    
    if (error.message.includes('amount')) {
      setErrors({ amount: error.message });
    } else {
      setErrors({ general: error.message || 'Failed to submit deposit request' });
    }
  } finally {
    setIsSubmitting(false);
  }
};

  // Success state
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center animate-bounce">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Request Sent! 🎉</h2>
          <p className="text-gray-600 mb-6">
            Your deposit request for <span className="font-bold text-green-600">${formData.amount}</span> has been sent to your parents for approval.
          </p>
          <div className="bg-blue-50 p-4 rounded-xl mb-6">
            <p className="text-blue-700 text-sm">
              💡 <strong>What happens next?</strong><br />
              Your parents will review and approve your deposit. Once approved, the money will be added to your garden!
            </p>
          </div>
          <Button 
            onClick={() => onNavigate('childDashboard', child)}
            className="w-full"
          >
            Back to My Garden
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/50">
      {/* Header */}
      <div className="border-b border-gray-200/80 bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onNavigate('childDashboard', child)}
                className="rounded-xl"
                disabled={isSubmitting}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Garden
              </Button>
              <div className="hidden sm:block w-px h-6 bg-gray-300"></div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Add Money</h1>
                <p className="text-gray-600 text-sm">{childName}'s Garden</p>
              </div>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <PiggyBank className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-8">
            <Card className="p-8">
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-4">
                    <Coins className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Add Money to Garden</h2>
                    <p className="text-gray-600">Tell us about the money you want to add</p>
                  </div>
                </div>
              </div>

              {/* General Error Display */}
              {errors.general && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                  <p className="text-red-600 text-sm font-medium">{errors.general}</p>
                </div>
              )}

              <div className="space-y-8">
                {/* Amount Section */}
                <div className="space-y-4">
                  <label className="block text-sm font-semibold text-gray-700">
                    💰 How much money do you want to add?
                  </label>
                  
                  <div className="relative">
                    <Input
                      type="number"
                      icon={DollarSign}
                      value={formData.amount}
                      onChange={(e) => {
                        setFormData({...formData, amount: e.target.value});
                        setErrors({...errors, amount: undefined});
                      }}
                      error={errors.amount}
                      placeholder="0.00"
                      step="0.25"
                      min="0.25"
                      max="500"
                      disabled={isSubmitting}
                      className="text-2xl font-bold"
                    />
                  </div>

                  {/* Quick Amount Buttons */}
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                    {quickAmounts.map((amount) => (
                      <button
                        key={amount}
                        onClick={() => {
                          setFormData({...formData, amount: amount.toString()});
                          setErrors({...errors, amount: undefined});
                        }}
                        className={`
                          p-3 rounded-xl border-2 text-center transition-all duration-200 font-semibold
                          ${formData.amount === amount.toString()
                            ? 'border-green-400 bg-green-50 text-green-700 shadow-md' 
                            : 'border-gray-200 hover:border-green-300 hover:bg-green-50 text-gray-700'
                          }
                          ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                        disabled={isSubmitting}
                      >
                        ${amount}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Source Section */}
                <div className="space-y-4">
                  <label className="block text-sm font-semibold text-gray-700">
                    🌟 Where did this money come from?
                  </label>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {moneySources.map((source) => {
                      const isSelected = formData.source === source.id;
                      
                      return (
                        <button
                          key={source.id}
                          onClick={() => {
                            setFormData({...formData, source: source.id});
                            setErrors({...errors, source: undefined});
                            
                            // Auto-fill description for non-other sources
                            if (source.id !== 'other') {
                              setFormData(prev => ({...prev, source: source.id, description: source.name}));
                            }
                          }}
                          className={`
                            p-4 rounded-2xl border-2 text-left transition-all duration-300 group
                            ${isSelected 
                              ? 'border-green-400 bg-green-50 shadow-lg scale-105' 
                              : 'border-gray-200 hover:border-green-300 hover:shadow-md hover:bg-green-50'
                            }
                            ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
                          `}
                          disabled={isSubmitting}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="text-2xl">{source.icon}</div>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-800">{source.name}</div>
                              <div className="text-xs text-gray-500">{source.description}</div>
                            </div>
                            {isSelected && (
                              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-4 h-4 text-white" />
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  {errors.source && (
                    <p className="text-sm text-red-600 font-medium">{errors.source}</p>
                  )}
                </div>

                {/* Custom Description for "Other" */}
                {formData.source === 'other' && (
                  <div className="space-y-2">
                    <Input
                      label="📝 Tell us more about this money"
                      value={formData.description}
                      onChange={(e) => {
                        setFormData({...formData, description: e.target.value});
                        setErrors({...errors, description: undefined});
                      }}
                      error={errors.description}
                      placeholder="Explain where this money came from..."
                      disabled={isSubmitting}
                      maxLength={100}
                    />
                    <p className="text-xs text-gray-500">
                      Be specific so your parents can understand and approve it!
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-6 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      onClick={handleSubmit}
                      size="lg"
                      className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                      disabled={isSubmitting || !formData.amount || !formData.source}
                    >
                      {isSubmitting ? 'Sending Request...' : 'Send to Parents for Approval'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="lg"
                      onClick={() => onNavigate('childDashboard', child)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Info Sidebar */}
          <div className="space-y-6">
            {/* Current Balance */}
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <PiggyBank className="w-5 h-5 mr-2 text-pink-500" />
                Current Balance
              </h3>
              <div className="text-center">
                <div className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent mb-2">
                  ${(child?.total_saved || child?.totalSaved || 0).toFixed(2)}
                </div>
                <p className="text-gray-600 text-sm">In your garden</p>
              </div>
            </Card>

            {/* How it Works */}
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <Star className="w-5 h-5 mr-2 text-yellow-500" />
                How it Works
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Tell us about your money</p>
                    <p className="text-gray-600 text-sm">Choose the amount and where it came from</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Parents review</p>
                    <p className="text-gray-600 text-sm">Your parents will check and approve it</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Money added</p>
                    <p className="text-gray-600 text-sm">Your garden balance grows!</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Tips */}
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <Heart className="w-5 h-5 mr-2 text-red-500" />
                Tips for Success
              </h3>
              <div className="space-y-3 text-sm">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-blue-700">
                    💡 <strong>Be honest!</strong> Only add money you actually received.
                  </p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-green-700">
                    🌟 <strong>Be specific!</strong> Tell your parents exactly where the money came from.
                  </p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-purple-700">
                    🚀 <strong>Be patient!</strong> Your parents will review it as soon as they can.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};